import { useCallback, useMemo, useState } from 'react';
import useBreakpoint from 'hooks/useBreakpoint';
import dynamic from 'next/dynamic';

// Interfaces
import type { Company } from 'interfaces/content/Company';
import type { MoreOnArticle } from 'interfaces/content/articles/Articles';
import type { Videos } from 'interfaces/content/Media';
import type { FranchiseItemDataTable } from 'interfaces/content/Ranking';
import { PageType } from 'interfaces/content/articles/Post';

// Constants
import { PaidModules } from 'constants/constants';
import getCompanyPageTestId from 'constants/testsIds/companyPage';
import { UIFragments } from 'utils/log/constants/uiFragments';
import { AD_TAGS } from 'components/PaidPlacement';

// Utils
import type { CompanyRankingList } from 'api/fragments';

// Components
import InStream from 'components/Ad/AdSlot/InStream';
import ErrorBoundary from 'components/ErrorBoundary';

// Styles
import { GridVideos } from 'components/Company/Stories/StoriesStyles';
import * as S from './CompanyPageStyles';

const Breadcrumbs = dynamic(() => import('components/Breadcrumbs'));
const Tabs = dynamic(() => import('components/Globals/Tabs'));
const Accordion = dynamic(() => import('components/Globals/Accordion'));
const PresentedBy = dynamic(() => import('components/PresentedBy'));
const SingleTicker = dynamic(() => import('components/Company/SingleTicker'));
const Accolades = dynamic(() => import('components/Company/Accolades'));
const AboutCompany = dynamic(() => import('components/Company/AboutCompany'));
const SectionTitle = dynamic(() => import('components/Globals/SectionTitle'));

const LatestArticles = dynamic(() => import('components/Company/LatestArticles'), {
  ssr: false,
});
const InRankings = dynamic(() => import('components/Company/InRankings'), {
  ssr: false,
});
const Stats = dynamic(() => import('components/Company/Stats'), {
  ssr: false,
});
const Stories = dynamic(() => import('components/Company/Stories'), {
  ssr: false,
});
const StockQuotesChart = dynamic(() => import('components/Company/StockQuotesChart'), {
  ssr: false,
});

const ExecutiveProfiles = dynamic(() => import('components/Company/ExecutiveProfiles'), {
  ssr: false,
});
const TwitterFeed = dynamic(() => import('components/Company/TwitterFeed'), {
  ssr: false,
});
const LinkedInFeed = dynamic(() => import('components/Company/LinkedInFeed'), {
  ssr: false,
});
const VodVideo = dynamic(() => import('components/VodVideo'), {
  ssr: false,
});
const LatestVideo = dynamic(() => import('components/Company/Videos'), {
  ssr: false,
});
const StnVideo = dynamic(() => import('components/StnVideo'), {
  ssr: false,
});

export interface Props {
  company: Company;
  latestNews: MoreOnArticle[];
  videos: Videos;
  mostPopularNews: MoreOnArticle[];
  previewKey?: string;
}
const SOCIAL_ACCOUNT = {
  LinkedIn: 'linkedin',
  Twitter: 'twitter',
};

const CompanyPage: React.FC<Props> = ({ company, latestNews, videos, mostPopularNews, previewKey }: Props) => {
  const isMobile = useBreakpoint('mdMax');
  const [activeAccordion, setActiveAccordion] = useState<number | null>(0);
  const { dataTables: stats, socialAccounts, adTags } = company;
  const adTagNames = adTags.map((tag) => tag.name);
  const noLatestNews = adTagNames.includes(AD_TAGS.NO_LATEST_NEWS);
  const noMostPopularNews = adTagNames.includes(AD_TAGS.NO_MOST_POPULAR_NEWS);

  const getSocialAccount = useCallback(
    (type: string) =>
      Array.isArray(socialAccounts) &&
      (socialAccounts?.find(({ name }) => name === type) as
        | { embedId: string | null; name: string; account: string }
        | undefined),
    [socialAccounts],
  );

  const twitterAccount = getSocialAccount(SOCIAL_ACCOUNT.Twitter);

  const linkedInAccount = getSocialAccount(SOCIAL_ACCOUNT.LinkedIn);

  const statsData = useMemo(
    () => ({
      changeHeader: '',
      footnote: company.companyInfo.Footnote,
      title: 'Company Information',
      valueHeader: '',
      values: Object.keys(company.companyInfo)
        .filter((key) => key !== 'Footnote' && key !== 'gptwProfile' && key !== 'StockQuote')
        .map((key) => ({
          title: key,
          value: company.companyInfo[key],
        })),
    }),
    [company.companyInfo],
  );

  const restOfTabs = stats
    .filter(
      (stat) =>
        stat.values.length &&
        stat.title.toLowerCase() !== 'company information' &&
        stat.title.toLowerCase() !== 'company info',
    )
    .map(({ changeHeader, title, footnote, valueHeader, values }: FranchiseItemDataTable) => ({
      children: <Stats data={{ changeHeader, footnote, title: '', valueHeader, values }} />,
      title,
    }));

  const yearFilteredRankings = company.yearFilteredRankings.filter(
    (item, index) => index === company.companyRankingLists.findIndex((obj) => item.title === obj.title),
  );

  const changeTheWorldTab = [];
  company.yearFilteredRankings.forEach((item) => {
    if (item.title.toLocaleLowerCase() !== 'change the world') {
      return;
    }

    if (!changeTheWorldTab.length) {
      changeTheWorldTab[0] = item;
      return;
    }

    if (changeTheWorldTab[0].year < item.year) {
      changeTheWorldTab[0] = item;
    }
  });

  const shiftF500AndG500 = (rankingList: CompanyRankingList[]) => {
    const priorityTitles = ['Fortune 500', 'Global 500'];

    return rankingList.sort((itemA, itemB) => {
      const aIndex = priorityTitles.indexOf(itemA.title);
      const bIndex = priorityTitles.indexOf(itemB.title);

      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      return 0;
    });
  };

  const tabsConfig = [...restOfTabs];

  if (statsData.values.length) {
    tabsConfig.unshift({
      children: <Stats data={statsData} />,
      title: 'Company Information',
    });
  }

  const onAccordionClick = (index: number) => {
    const activeItem = activeAccordion === index;
    setActiveAccordion(activeItem ? null : index);
  };

  const gptwProfileLink = company.companyInfo.gptwProfile;
  const slugRegex = /\/company(\/)+|\/?$/g;
  const slug = company.slug.replace(slugRegex, '');

  const stockSymbol = company.companyInfo.StockQuote
    ? `${company.companyInfo.StockQuote}:${company.companyInfo.Ticker}`
    : `${company.companyInfo.Ticker}`;

  const storeDataCy = new Map([
    ['titleDataCy', getCompanyPageTestId('MOST_POPULAR_TITLE')],
    ['cardDataCy', getCompanyPageTestId('MOST_POPULAR_CARD')],
    ['exploreMoreDataCy', getCompanyPageTestId('MOST_POPULAR_EXPLORE_MORE')],
  ]);

  return (
    <S.Wrapper
      id='content'
      className='franchise-content'
    >
      {company.sponsorData?.showBanner && (
        <PresentedBy
          logo={company.sponsorData?.bannerImage}
          linkTo={company.sponsorData.bannerImageLink}
          sponsorType='sponsor'
          name={company.title}
        />
      )}
      <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
        <Breadcrumbs {...company.breadcrumbs} />
      </ErrorBoundary>
      {/* About Section */}
      <S.GridContainer>
        <div>
          <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
            <S.Header
              as='h1'
              data-cy={getCompanyPageTestId('COMPANY_TITLE')}
              $fontFamily='graphikCond'
              $size={{ default: 'level1' }}
            >
              {company.title}
            </S.Header>
          </ErrorBoundary>
          {company.companyInfo.Ticker && (
            <S.TickerWrapper>
              <SingleTicker symbol={stockSymbol} />
            </S.TickerWrapper>
          )}
        </div>
        <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
          {yearFilteredRankings.length > 0 && !isMobile && (
            <Accolades
              titleDataCy={getCompanyPageTestId('ACCOLADES_TITLE')}
              wrapperDataCy={getCompanyPageTestId('ACCOLADES_CONTAINER')}
              rankDataCy={getCompanyPageTestId('ACCOLADES_RANK')}
              labelDataCy={getCompanyPageTestId('ACCOLADES_LABEL')}
              linkDataCy={getCompanyPageTestId('ACCOLADES_LINK')}
              rankings={shiftF500AndG500(yearFilteredRankings)}
              companyTitle={company.title}
            />
          )}
        </ErrorBoundary>
        <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
          <AboutCompany
            tabDataCy={getCompanyPageTestId('ABOUT_TAB')}
            contentDataCy={getCompanyPageTestId('ABOUT_CONTENT')}
            companyTitle={company.title}
            description={company.description}
            aboutFeaturedImage={company.image}
            gptwProfileLink={gptwProfileLink}
            greatPlaceFeaturedData={company.greatPlaceToWorkRankings}
            changeTheWorldData={changeTheWorldTab}
            earningsReports={company?.earningReports}
            rankings={shiftF500AndG500(yearFilteredRankings)}
          />
        </ErrorBoundary>
      </S.GridContainer>

      {/* Company info section */}
      <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
        {isMobile ? (
          <S.AccordionsContainer>
            {[statsData, ...stats].map((statData, index) => (
              <Accordion
                key={statData.title}
                index={index}
                isExpanded={activeAccordion === index}
                title={statData.title}
                onClick={() => onAccordionClick(index)}
              >
                {index === 0 ? (
                  <Stats data={statsData} />
                ) : (
                  <Stats
                    data={{
                      changeHeader: statData?.changeHeader || '',
                      title: '',
                      valueHeader: statData?.valueHeader || '',
                      values: statData.values,
                    }}
                  />
                )}
              </Accordion>
            ))}
          </S.AccordionsContainer>
        ) : (
          <S.TabsContainer>
            <Tabs
              tabDataCy={getCompanyPageTestId('COMPANY_INFORMATION')}
              config={tabsConfig}
              defaultIndex={0}
              className='tabs'
              enableTabsSlider
            />
          </S.TabsContainer>
        )}
      </ErrorBoundary>
      <InStream
        index={0}
        pageType={PageType.COMPANY_V2}
        renderAds
      />

      {/* Executive Profiles Section */}
      <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
        {company?.paidModules?.includes(PaidModules.ExecutiveProfiles) && company.executiveProfiles && (
          <ExecutiveProfiles profiles={company.executiveProfiles} />
        )}
      </ErrorBoundary>

      {/* Stock Chart Section */}
      <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
        {company.companyInfo.Ticker && (
          <S.SectionWrapper $gap={8}>
            <SectionTitle
              tag='h2'
              text={`${company.title} stock chart`}
              variant='border'
              titleDataCy={getCompanyPageTestId('ACCOLADES_TITLE')}
            />
          </S.SectionWrapper>
        )}
      </ErrorBoundary>
      <S.Container>
        <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
          {company.companyInfo.Ticker && (
            <StockQuotesChart
              symbol={stockSymbol}
              className='stock-chart'
            />
          )}
        </ErrorBoundary>

        <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
          {latestNews.length > 0 && !noLatestNews && (
            <LatestArticles
              companyName={company.title}
              articles={latestNews}
              className={`latest-module ${company.companyInfo.Ticker ? 'no-padding' : ''}`}
            />
          )}
        </ErrorBoundary>
      </S.Container>

      {/* In Rankings Section */}
      <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
        {company.companyRankingLists.length > 0 && (
          <InRankings
            companyName={company.title}
            rankings={company.companyRankingLists}
            companySlug={slug}
            previewKey={previewKey}
          />
        )}
      </ErrorBoundary>

      {/* Nativo Section */}
      <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
        {company?.paidModules?.includes(PaidModules.Nativo) && (
          <S.NativoWrapper
            as='aside'
            className='nativo-company'
            aria-label='Sponsored content'
          >
            <SectionTitle
              tag='h2'
              text={`Content from ${company.title}`}
              variant='border'
            />

            <div
              className='nativo-ads'
              aria-live='polite'
            >
              <S.ContentWrapper />
              <S.ContentWrapper />
              <S.ContentWrapper />
              <S.ContentWrapper />
            </div>
          </S.NativoWrapper>
        )}
      </ErrorBoundary>

      {/* Video + Ad Section */}
      <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
        {company.stnVideoId && (
          <S.VideoContainer>
            <StnVideo
              videoId={company.stnVideoId}
              adTags={adTags}
            />
            <InStream
              index={1}
              renderAds
              aria-label='Sponsored Content - Advertisement'
            />
          </S.VideoContainer>
        )}
      </ErrorBoundary>

      <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
        {company.vodVideoId && !company.stnVideoId && (
          <S.VideoContainer>
            <VodVideo
              videoId={company.vodVideoId}
              className='company-video'
            />
            <InStream
              index={1}
              renderAds
              aria-label='Sponsored Content - Advertisement'
            />
          </S.VideoContainer>
        )}
      </ErrorBoundary>

      {/* Twitter Section */}
      <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
        {company?.paidModules?.includes(PaidModules.Twitter) && twitterAccount && (
          <TwitterFeed
            companyName={company.title}
            companyUsername={twitterAccount.account}
          />
        )}
      </ErrorBoundary>

      {/* Linkedin Section */}
      <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
        {linkedInAccount && linkedInAccount.embedId && (
          <LinkedInFeed
            companyName={company.title}
            dataWidgetId={linkedInAccount?.embedId}
          />
        )}
      </ErrorBoundary>

      {/* Most Popular News Section */}
      <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
        {mostPopularNews && mostPopularNews.length > 0 && !noMostPopularNews && (
          <Stories
            moreAboutContent={mostPopularNews.slice(0, 8)}
            companyName={company.title}
            className='most-popular-module'
            dataCy={storeDataCy}
          />
        )}
      </ErrorBoundary>

      {/* Ad Section */}
      <InStream
        index={2}
        pageType={PageType.COMPANY_V2}
        renderAds
        aria-label='Sponsored Content - Advertisement'
      />

      {/* Videos Section */}
      <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
        {videos.total > 0 && (
          <S.SectionWrapper $gap={24}>
            <SectionTitle
              tag='h2'
              text='Videos'
              variant='border'
              titleDataCy={getCompanyPageTestId('VIDEOS_TITLE')}
            />
            <GridVideos
              $articleCount={mostPopularNews.length}
              data-cy={getCompanyPageTestId('VIDEOS_CONTAINER')}
            >
              {videos.videos.map((video) => (
                <LatestVideo
                  key={video.videoId}
                  {...video}
                />
              ))}
            </GridVideos>
          </S.SectionWrapper>
        )}
      </ErrorBoundary>

      {/* Coupons Section*/}
      <ErrorBoundary fragment={UIFragments.COMPANY_PAGE}>
        {/* since this is always false commented it out
        {couponLinkValue && false  &&
          <CouponLink
            companyName={company.title}
            couponLink={couponLinkValue}
          />
        }
        */}
      </ErrorBoundary>
    </S.Wrapper>
  );
};

export default CompanyPage;
